import type { Principal } from './PrincipalContext';

// ── Stored principal choice ──────────────────────────────────────────
// The session key always holds JSON — exactly one of:
//   {"myView":true}       the EA deliberately chose "My View"
//   {"principal":{...}}   the EA is acting for that principal
// Absent = no choice made yet (arms the sole-principal auto-select in
// PrincipalContext). This module is the ONLY place allowed to touch the
// key — AuthProvider (sign-out) and PrincipalContext both import from
// here rather than hardcoding the literal. Standalone module so the
// AuthProvider ↔ PrincipalContext import graph stays acyclic.

const SESSION_KEY = 'cadence_activePrincipal';

export type StoredChoice = { myView: true } | { principal: Principal };

export function readStoredChoice(): StoredChoice | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    // Legacy value from before the JSON envelope (pre-2026-08-26 sessions).
    if (raw === '__my_view__') return { myView: true };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      if (parsed.myView === true) return { myView: true };
      if (parsed.principal && typeof parsed.principal.id === 'string')
        return { principal: parsed.principal as Principal };
      // Legacy: a bare serialized Principal.
      if (typeof parsed.id === 'string' && typeof parsed.name === 'string')
        return { principal: parsed as Principal };
    }
  } catch { /* corrupted value — treat as unset */ }
  return null;
}

export function writeStoredChoice(choice: StoredChoice) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(choice));
  } catch { /* private browsing — selection just won't survive a reload */ }
}

/** Remove any stored principal selection. Used by sign-out (AuthProvider). */
export function clearStoredPrincipal() {
  sessionStorage.removeItem(SESSION_KEY);
}
